import React, { useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';

// Styled components
const VocationalContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem;
`;

const HeroSection = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 4rem;
  padding: 3rem 1rem;
  border-radius: 16px;
  background: linear-gradient(135deg, #3b82f6, #8b5cf6);
  color: white;
  position: relative;
  overflow: hidden;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  z-index: 2;
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.2rem;
  max-width: 700px;
  margin: 0 auto 2rem;
  opacity: 0.9;
  z-index: 2;
  line-height: 1.6;
`;

const CourseGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
  margin: 3rem 0;
`;

const CourseCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid #e2e8f0;
`;

const CourseImage = styled(motion.img)`
  width: 100%;
  height: 200px;
  object-fit: cover;
`;

const CourseContent = styled.div`
  padding: 1.5rem;
`;

const CourseTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: #1e293b;
`;

const CourseDescription = styled.p`
  color: #64748b;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`;

const CourseMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
`;

const CourseDuration = styled.span`
  font-size: 0.875rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const EnrollButton = styled(motion.button)`
  padding: 0.75rem 1.5rem;
  background: linear-gradient(to right, #3b82f6, #8b5cf6);
  color: white;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  font-size: 0.875rem;
`;

const FilterBar = styled(motion.div)`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const FilterButton = styled(motion.button)`
  padding: 0.5rem 1.5rem;
  background: ${props => props.active ? '#3b82f6' : '#e2e8f0'};
  color: ${props => props.active ? 'white' : '#1e293b'};
  border: none;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
`;

const DecorationCircle = styled(motion.div)`
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.1);
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      duration: 0.6
    }
  }
};

const Vocational = () => {
  const [activeFilter, setActiveFilter] = useState('All');
  const [hoveredCourse, setHoveredCourse] = useState(null);

  const courses = [
    {
      id: 1,
      title: "Electrician Training",
      description: "Comprehensive electrical systems training with hands-on practice and certification preparation.",
      duration: "6 Months",
      category: "Technical",
      image: "https://images.unsplash.com/photo-1581093450021-4a7360e9a7e8?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80"
    },
    {
      id: 2,
      title: "Culinary Arts",
      description: "Professional cooking techniques, food safety, and kitchen management for aspiring chefs.",
      duration: "8 Months",
      category: "Hospitality",
      image: "https://images.unsplash.com/photo-1606787366850-de6330128bfc?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80"
    },
    {
      id: 3,
      title: "Automotive Repair",
      description: "Modern vehicle maintenance and repair skills with industry-standard equipment training.",
      duration: "9 Months",
      category: "Technical",
      image: "https://images.unsplash.com/photo-1492144534655-ae79c964c9d7?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80"
    },
    {
      id: 4,
      title: "Healthcare Assistant",
      description: "Patient care fundamentals, medical terminology, and clinical procedures for healthcare support roles.",
      duration: "5 Months",
      category: "Medical",
      image: "https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80"
    },
    {
      id: 5,
      title: "Graphic Design",
      description: "Digital design principles, Adobe Creative Suite, and portfolio development for creative careers.",
      duration: "7 Months",
      category: "Creative",
      image: "https://images.unsplash.com/photo-1626785774573-4b799315345d?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80"
    },
    {
      id: 6,
      title: "HVAC Technician",
      description: "Heating, ventilation, and air conditioning systems installation, maintenance, and repair.",
      duration: "10 Months",
      category: "Technical",
      image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=600&q=80"
    }
  ];

  const categories = ['All', 'Technical', 'Hospitality', 'Medical', 'Creative'];

  return (
    <VocationalContainer>
      <HeroSection
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <DecorationCircle
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.3, duration: 0.8 }}
          style={{
            width: '300px',
            height: '300px',
            top: '-100px',
            left: '-100px'
          }}
        />
        <DecorationCircle
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          style={{
            width: '200px',
            height: '200px',
            bottom: '-50px',
            right: '-50px'
          }}
        />
        
        <HeroTitle
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.8 }}
        >
          Transform Your Career with Vocational Training
        </HeroTitle>
        
        <HeroSubtitle
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.8 }}
        >
          Hands-on courses designed to give you practical skills for in-demand jobs with industry-recognized certifications
        </HeroSubtitle>
      </HeroSection>

      <FilterBar
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.6, duration: 0.8 }}
      >
        {categories.map(category => (
          <FilterButton
            key={category}
            active={activeFilter === category}
            onClick={() => setActiveFilter(category)}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            {category}
          </FilterButton>
        ))}
      </FilterBar>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <CourseGrid>
          {courses
            .filter(course => activeFilter === 'All' || course.category === activeFilter)
            .map(course => (
              <CourseCard
                key={course.id}
                variants={itemVariants}
                whileHover={{ y: -10 }}
                onHoverStart={() => setHoveredCourse(course.id)}
                onHoverEnd={() => setHoveredCourse(null)}
              >
                <CourseImage
                  src={course.image}
                  alt={course.title}
                  animate={{
                    scale: hoveredCourse === course.id ? 1.05 : 1
                  }}
                  transition={{ duration: 0.3 }}
                />
                <CourseContent>
                  <CourseTitle>{course.title}</CourseTitle>
                  <CourseDescription>{course.description}</CourseDescription>
                  <CourseMeta>
                    <CourseDuration>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" strokeWidth="2"></circle>
                        <polyline points="12 6 12 12 16 14" strokeWidth="2"></polyline>
                      </svg>
                      {course.duration}
                    </CourseDuration>
                    <EnrollButton
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                    >
                      Enroll Now
                    </EnrollButton>
                  </CourseMeta>
                </CourseContent>
              </CourseCard>
            ))}
        </CourseGrid>
      </motion.div>
    </VocationalContainer>
  );
};

export default Vocational;